import { useMemo } from "react";
import { useNotifications, Notification } from "@/hooks/NotificationsProvider";
import { RxCross2 } from "react-icons/rx";
import { AnimatePresence, motion } from "framer-motion";
import { resolveThemeValue } from "@/lib/theme-resolve";
import { cn } from "@/lib/utils";

export function NotificationToasts({
  orientation = "horizontal",
}: {
  orientation?: "horizontal" | "vertical";
}) {
  const { notifications } = useNotifications();
  const stacked = orientation === "vertical";

  if (notifications.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      <div
        className={cn(
          stacked
            ? "flex shrink-0 flex-col gap-2 px-2 py-2"
            : "flex shrink-0 flex-col w-full"
        )}
      >
        {notifications.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            stacked={stacked}
          />
        ))}
      </div>
    </AnimatePresence>
  );
}

const NotificationToast = ({
  notification,
  stacked,
}: {
  notification: Notification;
  stacked?: boolean;
}) => {
  const { removeNotification } = useNotifications();
  const { bg, color } = useMemo(() => {
    if (notification.mode === "critical") {
      return { bg: "primary.accent", color: "primary.bgBase" };
    } else if (notification.mode === "warning") {
      return { bg: "warning.accent", color: "warning.bgBase" };
    } else {
      return { bg: "primary.element", color: "primary.textLow" };
    }
  }, [notification]);

  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0 }}
      transition={{ type: "spring", stiffness: 200, mass: 0.8, damping: 16 }}
    >
      <div
        className={cn(
          "flex w-full items-center py-2",
          stacked ? "mx-2 rounded" : "px-4"
        )}
        style={{ backgroundColor: resolveThemeValue(bg) as string }}
      >
        <div
          className={cn(
            "min-w-0 flex-1",
            stacked ? "px-4" : "flex items-center justify-center"
          )}
        >
          <div
            className={cn(
              "text-sm font-medium leading-normal tracking-wide",
              stacked
                ? "break-words text-pretty"
                : "flex flex-wrap items-center justify-center gap-x-4 gap-y-2"
            )}
            style={{ color: resolveThemeValue(color) as string }}
          >
            {notification.payload}
          </div>
        </div>
        {notification.dismissable && (
          <button
            type="button"
            className="flex shrink-0 cursor-pointer flex-col justify-center px-4"
            style={{ color: resolveThemeValue(color) as string }}
            onClick={() => removeNotification(notification.id)}
            aria-label="Dismiss notification"
          >
            <RxCross2 size={18} />
          </button>
        )}
      </div>
    </motion.div>
  );
};
